import { apiClient } from "../../../client";
import { StandardAxiosConfig, getCompressed, Filter, getBaseDataURL, parseDates } from "../../../common";
import { BaseDataModel } from "@domain/CRM/BaseDataModel";
export type ModelConstructor<T> = new () => T;

export class GetAPI_Base<T extends BaseDataModel> {
	protected baseUrl: string;
	private isCompressed: boolean = false;
	private ModelClass: ModelConstructor<T>;

	constructor(router: string, ModelClass: ModelConstructor<T>, isCompressed: boolean = false) {
		this.baseUrl = `${getBaseDataURL(router)}/get`;
		this.ModelClass = ModelClass;
		this.isCompressed = isCompressed;
	}

	private hydrateRecord(record: T): T {
		return Object.assign(new this.ModelClass(), record);
	}

	private hydrateRecords(records: T[]): T[] {
		return records.map((r) => Object.assign(new this.ModelClass(), r));
	}

	public async all(): Promise<T[]> {
		return this.byFilter();
	}

	public async byFilter(filter: Filter<T> = { fields: [] } as Filter<T>): Promise<T[]> {
		const url = `${this.baseUrl}${this.isCompressed ? "?compressed=true" : ""}`;

		if (this.isCompressed) {
			const records = (await getCompressed(url, filter)) as T[];
			parseDates<T>(records);
			return this.hydrateRecords(records);
		} else {
			const response = await apiClient.post(url, filter, StandardAxiosConfig());
			const records = response.data as T[];
			parseDates<T>(records);
			return this.hydrateRecords(records);
		}
	}

	public async byIds(ids: string[]): Promise<T[]> {
		const filter = {
			type: "OR",
			fields: ids.map((id) => ({ id: id })),
		} as Filter<T>;

		const records = await this.byFilter(filter);
		parseDates<T>(records);
		return records;
	}

	public async byId(id: string, isCompressed: boolean = this.isCompressed): Promise<T> {
		const queryParams: string[] = [];
		if (isCompressed) queryParams.push(`compressed=true`);
		const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";

		const url = `${this.baseUrl}/${id}${queryString}`;

		if (isCompressed) {
			const record = await getCompressed(url);
			if (Array.isArray(record)) {
				const r = record[0] as T;
				parseDates<T>([r]);
				return this.hydrateRecord(r);
			} else {
				const r = record as T;
				parseDates<T>([r]);
				return this.hydrateRecord(r);
			}
		} else {
			const response = await apiClient.get(url, StandardAxiosConfig());
			const record = response.data as T;
			parseDates<T>([record]);
			return this.hydrateRecord(record);
		}
	}
}
